import { Box, useDisclosure } from "@chakra-ui/react";
import { InfoSourceModal } from "../AnalyticsBlocks/AnalyticsModals/InfoSourceModal";
import { useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { fetchCandidateList } from "../../store/slices/analytics";

export interface SourceItem {
    name: string;
    color: string;
    count: number;
    id: number
}

interface ColoredBarProps {
    data: SourceItem[];
    status_id: number;
    vacancy_status: string;
    isSinglePage?: boolean
    vacancy_id?: number
}

export function ColoredBar({ data, vacancy_status, status_id, isSinglePage = false, vacancy_id }: ColoredBarProps) {
    const { isOpen: isSourceOpen, onOpen: onSourceOpen, onClose: onSourceClose } = useDisclosure();

    const [currentStatus, setCurrentStatus] = useState<string | null>(null)
    const [currentSource, setCurrentSource] = useState<string | null>(null)
    const { customer, period_from, period_to, recruiter, currentDepartment, status, currentChannel } = useAppSelector(
        (state: RootState) => state.analytics
    );

    const dispatch = useAppDispatch();

    const total = data.reduce((acc: number, item: SourceItem) => acc + item.count, 0);

    const maxWidth = 700;

    const calculatedWidth = Math.min(total * 20, maxWidth);

    const widthStyle = `${calculatedWidth}px`;

    return (
        <Box 
            display="flex" 
            width={widthStyle} 
            height="24px" 
            gap="2px"
            borderRadius="12px"
            overflow="hidden"
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
            border="1px solid rgba(255, 255, 255, 0.2)"
            position="relative"
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                pointerEvents: 'none',
                zIndex: 1
            }}
        >
            {data.map((item: SourceItem, index: number) => {
                const width = (item.count / total) * 100;
                const isFirst = index === 0;
                const isLast = index === data.length - 1;
                
                return (
                    <Box
                        key={index}
                        data-tooltip-id={"tooltip"}
                        data-tooltip-html={`${item.name}: ${item.count}`}
                        position="relative"
                        sx={{
                            width: `${width}%`,
                            height: '100%',
                            background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`,
                            borderRadius: isFirst ? '12px 0 0 12px' : isLast ? '0 12px 12px 0' : '0',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            "&:hover": {
                                transform: 'translateY(-1px)',
                                filter: 'brightness(1.1)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 2
                            },
                            "&:active": {
                                transform: 'translateY(0)',
                                filter: 'brightness(0.95)'
                            },
                            _before: {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '50%',
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)',
                                pointerEvents: 'none'
                            }
                        }}
                        onClick={() => {
                            setCurrentSource(item.name)
                            setCurrentStatus(vacancy_status)
                            dispatch(fetchCandidateList({ vacancy_application_status: status_id, source: item.id, period_from:period_from, period_to: period_to, recruiter_id: recruiter, customer_id: customer, vacancy_id: isSinglePage ? vacancy_id : null, department_id: currentDepartment, vacancy_status: Number(status), channel_id: currentChannel === 0 ? null : currentChannel }))
                            onSourceOpen()
                        }}
                    />
                );
            })}
            <InfoSourceModal isOpen={isSourceOpen} onClose={onSourceClose} status={currentStatus} source={currentSource} />
        </Box>
    );
}